import { useState } from 'react';
import { CalendarDays, Copy, TriangleAlert, Users } from 'lucide-react';
import {
  useCopyTimetableDayMutation,
  useCopyTimetableClassMutation,
  getApiError,
  type ClassTimetable,
} from '@/store/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Field, Select } from '@/components/ui/Field';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const DAYS = [
  { n: 1, label: 'Monday', short: 'Mon' },
  { n: 2, label: 'Tuesday', short: 'Tue' },
  { n: 3, label: 'Wednesday', short: 'Wed' },
  { n: 4, label: 'Thursday', short: 'Thu' },
  { n: 5, label: 'Friday', short: 'Fri' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  institutionId: string;
  groupId: string;
  className: string;
  groups: Array<{ _id: string; name: string }>;
  grid: ClassTimetable | undefined;
}

/*
  ═════════════════════════════════════════════════════════════════════════════
  COPYING — THE REASON THE GRID EVER GETS FILLED IN

  The arithmetic
  ──────────────
  Seven teaching periods × five days is 35 slots for one class. A ten-class
  school is 350. At a dialog each — open, pick subject, pick teacher, save —
  that is the better part of an hour of clicking before a single child has
  been taught anything, and it is the point at which a head teacher closes the
  tab and goes back to the Word document on the office computer.

  Real timetables repeat. The same subject sits at period 1 all week; lower
  classes share a shape and differ only in who takes them. Two copies turn the
  hour into about four minutes.

  Why both of these REPLACE rather than merge
  ───────────────────────────────────────────
  "Copy Monday to Tuesday" has one obvious meaning: Tuesday ends up looking
  like Monday. A merge would leave whatever Tuesday had in periods Monday
  does not use, and the result is a day that matches neither — the sort of
  half-state somebody discovers three weeks later. So the target is cleared
  first, and because that destroys work this dialog says exactly what will be
  overwritten before it will do it.

  Why a clashing teacher is dropped instead of refusing the copy
  ─────────────────────────────────────────────────────────────
  Two worse options: refuse the whole copy over one conflict and throw away
  the work, or copy the clash and put somebody in two rooms at once — the one
  thing the rest of this module exists to prevent. So the lesson copies, the
  name comes off it, and the count is reported, because a copy that quietly
  staffed nobody is worse than one that refused out loud.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function CopyTimetableDialog({
  open,
  onClose,
  institutionId,
  groupId,
  className,
  groups,
  grid,
}: Props) {
  const [mode, setMode] = useState<'day' | 'class'>('day');
  const [fromDay, setFromDay] = useState(1);
  const [toDays, setToDays] = useState<number[]>([]);
  const [toGroupId, setToGroupId] = useState('');
  const [result, setResult] = useState<string | null>(null);

  const [copyDay, dayState] = useCopyTimetableDayMutation();
  const [copyClass, classState] = useCopyTimetableClassMutation();
  const error = dayState.error ?? classState.error;
  const busy = dayState.isLoading || classState.isLoading;

  const slots = grid?.slots ?? [];
  const onFromDay = slots.filter((s) => s.day === fromDay).length;
  const others = groups.filter((g) => g._id !== groupId);

  /* What the target currently holds, so "this will be replaced" is a number
     and not a vague warning. */
  const willReplace =
    mode === 'day'
      ? toDays.reduce((n, d) => n + slots.filter((s) => s.day === d).length, 0)
      : null;

  const reset = () => {
    setToDays([]);
    setToGroupId('');
    setResult(null);
  };

  const run = async () => {
    setResult(null);
    const r =
      mode === 'day'
        ? await copyDay({ institutionId, groupId, fromDay, toDays })
        : await copyClass({ institutionId, fromGroupId: groupId, toGroupId });

    if ('error' in r) return;
    const { copied, teachersDropped } = r.data;
    setResult(
      teachersDropped === 0
        ? `${copied} lesson${copied === 1 ? '' : 's'} copied.`
        : `${copied} lesson${copied === 1 ? '' : 's'} copied. ${teachersDropped} ` +
          `${teachersDropped === 1 ? 'has' : 'have'} no teacher yet — whoever takes ` +
          `${teachersDropped === 1 ? 'it' : 'them'} was already busy at that time.`,
    );
    setToDays([]);
    setToGroupId('');
  };

  const canRun = mode === 'day' ? toDays.length > 0 && onFromDay > 0 : Boolean(toGroupId);

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Copy timetable"
      description="Fill a week quickly instead of one slot at a time"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Close
          </Button>
          <Button variant="primary" loading={busy} disabled={!canRun} onClick={() => void run()}>
            <Copy size={13} className="mr-1" />
            {mode === 'day'
              ? `Copy ${DAYS.find((d) => d.n === fromDay)?.label}`
              : 'Copy this week'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <div className="flex items-center gap-0.5 rounded-md border border-border bg-muted/50 p-0.5">
          {([
            { v: 'day' as const, label: 'A day to other days', icon: CalendarDays },
            { v: 'class' as const, label: 'This week to another class', icon: Users },
          ]).map(({ v, label, icon: Icon }) => (
            <button
              key={v}
              type="button"
              onClick={() => {
                setMode(v);
                reset();
              }}
              aria-pressed={mode === v}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 rounded px-2.5 py-1.5 text-[11px] font-semibold transition-colors',
                mode === v ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>

        {mode === 'day' ? (
          <>
            <Field label="Copy this day" htmlFor="copy-from">
              <Select id="copy-from" value={fromDay} onChange={(e) => setFromDay(Number(e.target.value))}>
                {DAYS.map((d) => {
                  const n = slots.filter((s) => s.day === d.n).length;
                  return (
                    <option key={d.n} value={d.n}>
                      {d.label} — {n} lesson{n === 1 ? '' : 's'}
                    </option>
                  );
                })}
              </Select>
            </Field>

            {onFromDay === 0 && (
              <p className={cn(styles.meta, 'text-[hsl(var(--record-ink-warning))]')}>
                {DAYS.find((d) => d.n === fromDay)?.label} has no lessons yet, so there is nothing
                to copy from it.
              </p>
            )}

            <Field label="Onto these days">
              <div className="flex flex-wrap gap-1.5">
                {DAYS.filter((d) => d.n !== fromDay).map((d) => {
                  const on = toDays.includes(d.n);
                  const has = slots.filter((s) => s.day === d.n).length;
                  return (
                    <button
                      key={d.n}
                      type="button"
                      onClick={() =>
                        setToDays((prev) =>
                          prev.includes(d.n) ? prev.filter((x) => x !== d.n) : [...prev, d.n],
                        )
                      }
                      aria-pressed={on}
                      className={cn(
                        'rounded-md border px-2.5 py-1.5 text-[11px] font-semibold transition-colors',
                        on
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-border text-muted-foreground hover:bg-muted/50',
                      )}
                    >
                      {d.short}
                      {has > 0 && (
                        <span className="ml-1 font-normal text-muted-foreground/80">({has})</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </Field>
          </>
        ) : (
          <>
            <Field
              label="Copy this class's whole week onto"
              htmlFor="copy-to-class"
              hint={`Every lesson from ${className}, Monday to Friday.`}
            >
              <Select id="copy-to-class" value={toGroupId} onChange={(e) => setToGroupId(e.target.value)}>
                <option value="">Choose a class…</option>
                {others.map((g) => (
                  <option key={g._id} value={g._id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            </Field>

            {/* Not a caveat — the expected outcome, and saying so here stops it
                reading as a bug when the new grid shows no names. */}
            <p className={styles.meta}>
              Subjects, periods and rooms copy across. Teachers do not: both classes meet at the
              same times, so anybody named here cannot also take the other class. Staff the new
              week after copying it.
            </p>
          </>
        )}

        {willReplace !== null && willReplace > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/[0.08] px-3 py-2.5">
            <TriangleAlert size={13} className="mt-[1px] flex-shrink-0 text-[hsl(var(--record-ink-warning))]" />
            <p className="text-[11px] leading-relaxed text-[hsl(var(--record-ink-warning))]">
              {willReplace} existing lesson{willReplace === 1 ? '' : 's'} on{' '}
              {toDays
                .map((d) => DAYS.find((x) => x.n === d)?.label)
                .filter(Boolean)
                .join(', ')}{' '}
              will be replaced.
            </p>
          </div>
        )}

        {mode === 'class' && toGroupId && (
          <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/[0.08] px-3 py-2.5">
            <TriangleAlert size={13} className="mt-[1px] flex-shrink-0 text-[hsl(var(--record-ink-warning))]" />
            <p className="text-[11px] leading-relaxed text-[hsl(var(--record-ink-warning))]">
              Whatever {others.find((g) => g._id === toGroupId)?.name} has timetabled now will be
              replaced.
            </p>
          </div>
        )}

        {error && (
          <p role="alert" className="text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]">
            {getApiError(error)}
          </p>
        )}

        {result && (
          <p
            role="status"
            className="rounded-lg border border-success/30 bg-success/[0.08] px-3 py-2.5 text-[11px] leading-relaxed text-[hsl(var(--record-ink-success))]"
          >
            {result}
          </p>
        )}
      </div>
    </Modal>
  );
}
